import { useGetAccountsQuery } from "features/api/accountApi.ts";
import { useParams } from "react-router";
import { SyntheticEvent, useContext, useMemo, useState } from "react";
import {
	AdminSearchAccountBullet,
	AdminSearchNoResultsFoundHeader,
	AdminSearchNoResultsFoundSubheader,
	BillTosPageHeaderText,
	SearchBillTosText
} from "constants/text.ts";
import { models } from "types/api/viewModels.ts";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch.tsx";
import { UserPermission } from "types/declarations/enums.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import APIErrorCustomBanner from "components/Common/APIErrorCustomBanner/APIErrorCustomBanner.tsx";
import {
	AdminSearchContainer,
	AdminSettingLandingPageHeader
} from "pages/Preferences/AdminSettings/AdminSettingLandingPage.styles.ts";
import { NoResultsBox } from "pages/Preferences/NoResultsBox.tsx";
import {
	AccountSettingsPageContent,
	AccountSettingsSearchWrapper
} from "pages/Preferences/Accounts/styles/AllAccountSettingsPage.styles.ts";
import { AccountBillToTable } from "pages/Preferences/Accounts/AccountBillToPage/components/AccountBillToTable/AccountBillToTable.tsx";
import { formatBillToDisplay } from "pages/Preferences/Accounts/AccountBillToPage/utils/AccountBillToPage.utils.ts";

const AccountBillToPage = () => {
	const { accountId } = useParams();
	const { data: accounts, isLoading } = useGetAccountsQuery(null);
	const { data: userDetails } = useGetUserDetailsQuery();
	const { configurationsData } = useContext(ConfigurationsContext);

	const [searchQuery, setSearchQuery] = useState("");

	const isAdmin = userDetails?.permissions?.includes(UserPermission.ADMIN) ?? false;
	const isUserPreferenceEnabled = configurationsData?.featureFlags?.userPreferenceEnabled ?? false;

	const account = useMemo(() => accounts?.find((acc) => acc.accountId === accountId), [accountId, accounts]);

	const billTos = useMemo(
		() => account?.billTos?.filter((billTo) => billTo?.accountId === accountId) ?? [],
		[accountId, account]
	);

	const filteredBillTos = useMemo(() => {
		const filtered = searchQuery
			? billTos.filter((billTo) => formatBillToDisplay(billTo).toLowerCase().includes(searchQuery.toLowerCase()))
			: billTos;

		// remove dupes
		const seen = new Set<string>();
		return filtered.filter((billTo) => {
			const displayString = formatBillToDisplay(billTo);
			if (seen.has(displayString)) {
				return false;
			}
			seen.add(displayString);
			return true;
		});
	}, [billTos, searchQuery]);

	const handleSearch = (_: SyntheticEvent, value: string | null) => {
		setSearchQuery(value ?? "");
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<AdminSearchContainer>
					<TableSkeleton />
				</AdminSearchContainer>
			);
		}

		if (filteredBillTos.length > 0) {
			return <AccountBillToTable billTos={filteredBillTos} />;
		}

		return (
			<NoResultsBox
				headerText={AdminSearchNoResultsFoundHeader}
				subheaderText={AdminSearchNoResultsFoundSubheader}
				bulletList={[AdminSearchAccountBullet]}
			/>
		);
	};

	if (!isAdmin || !isUserPreferenceEnabled) {
		return <APIErrorCustomBanner statusCode={403} />;
	}

	return (
		<div>
			<AdminSettingLandingPageHeader>
				<h1>
					{BillTosPageHeaderText} {formatBillToDisplay(account as models["CustomerAccountViewModel"])}
				</h1>
			</AdminSettingLandingPageHeader>
			<AccountSettingsPageContent>
				<AccountSettingsSearchWrapper>
					<SuggestedSearch
						ariaLabel="Admin Bill To Search"
						dataTestId="admin-billtos-search-input"
						dropDownOpenOnChar={0}
						placeholder={SearchBillTosText}
						id="admin-billtos-search-input"
						searchOptions={billTos.map(formatBillToDisplay)}
						searchValue={searchQuery}
						handleSearch={handleSearch}
						size="small"
						hasButton
					/>
				</AccountSettingsSearchWrapper>
				{renderContent()}
			</AccountSettingsPageContent>
		</div>
	);
};

export default AccountBillToPage;
